import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Button, Pagination, TextField, TableSortLabel, Checkbox,
  Dialog, DialogContent, DialogActions, DialogTitle,
  Container, Typography, Box, Grid, Card, CardContent, CardMedia,
  CardActions, Chip, IconButton, Fab, useMediaQuery, useTheme,
  InputAdornment, Menu, MenuItem, Divider, Badge, Avatar
} from '@mui/material';
import {
  Edit, Delete, Add, Search, FilterList, Close, Image as ImageIcon,
  Inventory2TwoTone, ShoppingCart, CheckCircle, Cancel
} from '@mui/icons-material';
import { API_BASE_URL, PRODUCTOELIMINAR, PRODUCTOSLISTS, RUTAPRODUCTOSIMAGENES } from '../services/apiConfig';
import { useNavigate } from "react-router-dom";
import { MenuInicial } from "./MenuInicial";
import Cargando from './utils/Cargando.jsx';

// Función para formatear precio (si no tienes formatCurrency)
const formatPrice = (price) => {
  return new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(price);
};

const ProductList = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [products, setProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage] = useState(isMobile ? 12 : 15);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [orderBy, setOrderBy] = useState('id');
  const [order, setOrder] = useState('asc');
  const [expandedImage, setExpandedImage] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [selectedRubro, setSelectedRubro] = useState('');
  const [rubros, setRubros] = useState([]);
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem('authToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const getDeleteUrl = () => {
    return API_BASE_URL.replace('/api/v2/', '/api/') + PRODUCTOELIMINAR;
  };

  const parseApiResponse = (response) => {
    const data = response?.data?.data ?? response?.data;
    return Array.isArray(data) ? data : [];
  };

  const handleEdit = (product) => {
    navigate('/ProductoAMC', { state: { productToEdit: product } });
  };

  const handleClickNew = () => {
    navigate('/productoAMC', { state: { productToEdit: null } });
  };

  useEffect(() => {
    const uniqueRubros = [...new Set(products.map((product) => product.rubro))];
    setRubros(uniqueRubros);
  }, [products]);

  const handleDeleteConfirmation = (confirmed) => {
    if (confirmed) {
      handleDelete(selectedProductId);
    }
    setDeleteDialogOpen(false);
    setSelectedProductId(null);
  };

  const handleDelete = async (productId) => {
    try {
      const response = await axios.post(getDeleteUrl(), {
        productoid: productId,
        ruta: RUTAPRODUCTOSIMAGENES
      }, {
        headers: getAuthHeaders()
      });
      console.log('Producto eliminado:', response.data);
      fetchProducts();
    } catch (error) {
      console.error('Error al eliminar el producto:', error);
    }
  };

  const handleImageClick = (image) => {
    setExpandedImage(image);
  };

  const handleCloseImageDialog = () => {
    setExpandedImage(null);
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(API_BASE_URL + PRODUCTOSLISTS, {
        headers: getAuthHeaders()
      });
      const productsData = parseApiResponse(response);
      setProducts(productsData);
      setLoading(false);
      setRubros([...new Set(productsData.map((product) => product.rubro))]);
    } catch (error) {
      console.error('Error al cargar los productos', error);
      setLoading(false);
    }
  };

  const filteredProducts = Array.isArray(products)
    ? products
        .filter((product) => product.nombre.toLowerCase().includes(search.toLowerCase()))
        .filter((product) => selectedRubro === '' || product.rubro === selectedRubro)
    : [];

  useEffect(() => {
    const token = sessionStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
      return;
    }

    const uniqueRubros = [...new Set(products.map((product) => product.rubro))];
    setRubros(uniqueRubros);
    fetchProducts();
  }, []);

  const handleRubroChange = (rubro) => {
    setSelectedRubro(rubro);
    setCurrentPage(1);
    setFilterMenuAnchor(null);
  };

  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;

  const sortedProducts = filteredProducts.sort((a, b) => {
    if (order === 'asc') {
      return a[orderBy] > b[orderBy] ? 1 : -1;
    } else {
      return a[orderBy] < b[orderBy] ? 1 : -1;
    }
  });

  const currentProducts = sortedProducts.slice(indexOfFirstProduct, indexOfLastProduct);

  const paginate = (event, page) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setCurrentPage(1);
  };

  const handleSort = (columnId) => {
    if (columnId === orderBy) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(columnId);
      setOrder('asc');
    }
  };

  // ==========================================
  // VISTA MÓVIL CON CARDS
  // ==========================================
  const MobileView = () => (
    <Box sx={{ pb: isMobile ? 10 : 0 }}>
      {/* Barra de búsqueda y filtros sticky */}
      <Paper 
        elevation={2}
        sx={{ 
          p: 2, 
          mb: 2, 
          position: 'sticky', 
          top: isMobile ? 56 : 0, 
          zIndex: 100,
          bgcolor: 'background.paper'
        }}
      >
        {/* Título y contador */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            <Inventory2TwoTone sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
            Productos
          </Typography>
          <Chip 
            label={`${filteredProducts.length} items`}
            color="primary"
            size="small"
          />
        </Box>

        {/* Búsqueda */}
        <TextField
          placeholder="Buscar productos..."
          variant="outlined"
          fullWidth
          size="small"
          value={search}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 1.5 }}
        />

        {/* Filtro de categorías */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip
            label={selectedRubro || 'Todas las categorías'}
            onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
            onDelete={selectedRubro ? () => handleRubroChange('') : undefined}
            color={selectedRubro ? 'primary' : 'default'}
            icon={<FilterList />}
            sx={{ flexGrow: 1 }}
          />
        </Box>
      </Paper>

      {/* Menú desplegable de categorías */}
      <Menu
        anchorEl={filterMenuAnchor}
        open={Boolean(filterMenuAnchor)}
        onClose={() => setFilterMenuAnchor(null)}
        PaperProps={{
          sx: { maxHeight: 400, width: '80vw', maxWidth: 300 }
        }}
      >
        <MenuItem onClick={() => handleRubroChange('')}>
          <Typography fontWeight={!selectedRubro ? 600 : 400}>
            Todas las categorías
          </Typography>
        </MenuItem>
        <Divider />
        {rubros.map((rubro) => (
          <MenuItem 
            key={rubro} 
            onClick={() => handleRubroChange(rubro)}
            selected={selectedRubro === rubro}
          >
            <Typography fontWeight={selectedRubro === rubro ? 600 : 400}>
              {rubro}
            </Typography>
          </MenuItem>
        ))}
      </Menu>

      {/* Grid de productos en cards */}
      <Grid container spacing={2}>
        {currentProducts.map((product) => (
          <Grid item xs={6} sm={4} md={3} key={product.id}>
            <Card 
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                transition: 'all 0.2s',
                '&:hover': {
                  boxShadow: 6,
                  transform: 'translateY(-4px)',
                }
              }}
            >
              {/* Badge de estado en la esquina */}
              <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
                {product.activo === "1" || product.activo === 1 ? (
                  <Chip
                    icon={<CheckCircle />}
                    label="Activo"
                    color="success"
                    size="small"
                    sx={{ fontSize: '0.7rem', height: 24 }}
                  />
                ) : (
                  <Chip
                    icon={<Cancel />}
                    label="Inactivo"
                    color="default"
                    size="small"
                    sx={{ fontSize: '0.7rem', height: 24 }}
                  />
                )}
              </Box>

              {/* Imagen del producto */}
              <CardMedia
                component="img"
                height="160"
                image={`${RUTAPRODUCTOSIMAGENES}/${product.foto}`}
                alt={product.nombre}
                onClick={() => handleImageClick(`${RUTAPRODUCTOSIMAGENES}/${product.foto}`)}
                sx={{ 
                  cursor: 'pointer',
                  objectFit: 'cover',
                  bgcolor: 'grey.100'
                }}
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/160?text=Sin+Imagen';
                }}
              />

              {/* Contenido del card */}
              <CardContent sx={{ flexGrow: 1, pb: 1, px: 1.5, pt: 1.5 }}>
                {/* Categoría */}
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ 
                    display: 'block',
                    mb: 0.5,
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    fontWeight: 600
                  }}
                >
                  {product.rubro}
                </Typography>

                {/* Nombre del producto */}
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 600,
                    mb: 0.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    minHeight: '2.8em',
                    fontSize: '0.9rem',
                    lineHeight: 1.4
                  }}
                  title={product.nombre}
                >
                  {product.nombre}
                </Typography>

                {/* Precio destacado */}
                <Typography 
                  variant="h6" 
                  color="primary"
                  sx={{ 
                    fontWeight: 'bold',
                    mb: 0.5,
                    fontSize: '1.2rem'
                  }}
                >
                  ${formatPrice(product.precio)}
                </Typography>

                {/* Info adicional */}
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  <Chip
                    label={`Stock: ${product.unidades}`}
                    size="small"
                    variant="outlined"
                    color={product.unidades > 0 ? 'default' : 'error'}
                    sx={{ 
                      fontSize: '0.7rem',
                      height: 22
                    }}
                  />
                  <Chip
                    label={`ID: ${product.id}`}
                    size="small"
                    variant="outlined"
                    sx={{ 
                      fontSize: '0.7rem',
                      height: 22
                    }}
                  />
                </Box>
              </CardContent>

              {/* Acciones */}
              <CardActions sx={{ p: 1, pt: 0, gap: 0.5 }}>
                <Button 
                  size="small" 
                  onClick={() => handleEdit(product)}
                  startIcon={<Edit />}
                  variant="outlined"
                  fullWidth
                  sx={{ fontSize: '0.75rem' }}
                >
                  Editar
                </Button>
                <IconButton 
                  size="small" 
                  onClick={() => {
                    setSelectedProductId(product.id);
                    setDeleteDialogOpen(true);
                  }}
                  color="error"
                  sx={{ 
                    border: '1px solid',
                    borderColor: 'error.main',
                    borderRadius: 1
                  }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Mensaje si no hay productos */}
      {currentProducts.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Inventory2TwoTone sx={{ fontSize: 80, color: 'grey.300', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No se encontraron productos
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Intenta con otros filtros o búsqueda
          </Typography>
        </Box>
      )}

      {/* Paginación */}
      {sortedProducts.length > productsPerPage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={Math.ceil(sortedProducts.length / productsPerPage)}
            page={currentPage}
            onChange={paginate}
            color="primary"
            size={isMobile ? 'medium' : 'large'}
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* Botón flotante para nuevo producto */}
      <Fab
        color="primary"
        aria-label="add"
        onClick={handleClickNew}
        sx={{
          position: 'fixed',
          bottom: isMobile ? 80 : 16,
          right: 16,
          zIndex: 1000
        }}
      >
        <Add />
      </Fab>
    </Box>
  );

  // ==========================================
  // VISTA DESKTOP CON TABLA
  // ==========================================
  const DesktopView = () => (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Button onClick={handleClickNew} variant="contained" color="primary" startIcon={<Add />}>
          Nuevo Producto
        </Button>
      </Box>

      <TextField
        label="Buscar por nombre del producto"
        variant="outlined"
        fullWidth
        value={search}
        onChange={handleSearchChange}
        sx={{ mb: 2 }}
      />

      <TextField
        select
        label="Filtrar por rubro"
        variant="outlined"
        fullWidth
        value={selectedRubro}
        onChange={(e) => setSelectedRubro(e.target.value)}
        SelectProps={{ native: true }}
        sx={{ mb: 2 }}
      >
        <option value="">Todos los Rubros</option>
        {rubros.map((rubro) => (
          <option key={rubro} value={rubro}>
            {rubro}
          </option>
        ))}
      </TextField>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'id'}
                  direction={orderBy === 'id' ? order : 'asc'}
                  onClick={() => handleSort('id')}
                >
                  ID
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'nombre'}
                  direction={orderBy === 'nombre' ? order : 'asc'}
                  onClick={() => handleSort('nombre')}
                >
                  Nombre
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'rubro'}
                  direction={orderBy === 'rubro' ? order : 'asc'}
                  onClick={() => handleSort('rubro')}
                >
                  Rubro
                </TableSortLabel>
              </TableCell>
              <TableCell>Descripción</TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'unidades'}
                  direction={orderBy === 'unidades' ? order : 'asc'}
                  onClick={() => handleSort('unidades')}
                >
                  Stock
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'precio'}
                  direction={orderBy === 'precio' ? order : 'asc'}
                  onClick={() => handleSort('precio')}
                >
                  Precio
                </TableSortLabel>
              </TableCell>
              <TableCell>Precio Web</TableCell>
              <TableCell align="center">Web</TableCell>
              <TableCell>Activo</TableCell>
              <TableCell>Imagen</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currentProducts.map((product) => (
              <TableRow key={product.id}>
                <TableCell>{product.id}</TableCell>
                <TableCell>{product.nombre}</TableCell>
                <TableCell>{product.rubro}</TableCell>
                <TableCell>{product.descripcion}</TableCell>
                <TableCell>{product.unidades}</TableCell>
                <TableCell>${formatPrice(product.precio)}</TableCell>
                <TableCell>${product.precio_web ? formatPrice(product.precio_web) : '-'}</TableCell>
                <TableCell align="center">
                  <Chip 
                    label={product.mostrar_web == 1 || product.mostrar_web === '1' ? 'Sí' : 'No'} 
                    color={product.mostrar_web == 1 || product.mostrar_web === '1' ? 'success' : 'default'} 
                    size="small" 
                  />
                </TableCell>
                <TableCell>
                  <Checkbox checked={product.activo === "1" || product.activo === 1} />
                </TableCell>
                <TableCell>
                  <img
                    src={`${RUTAPRODUCTOSIMAGENES}/${product.foto}`}
                    alt={product.nombre}
                    style={{ maxWidth: '50px', maxHeight: '50px', cursor: 'pointer' }}
                    onClick={() => handleImageClick(`${RUTAPRODUCTOSIMAGENES}/${product.foto}`)}
                  />
                </TableCell>
                <TableCell>
                  <Button onClick={() => handleEdit(product)} size="small">Editar</Button>
                  <Button 
                    onClick={() => {
                      setSelectedProductId(product.id);
                      setDeleteDialogOpen(true);
                    }}
                    size="small"
                    color="error"
                  >
                    Eliminar
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
        <Pagination
          count={Math.ceil(sortedProducts.length / productsPerPage)}
          page={currentPage}
          onChange={paginate}
          color="primary"
        />
      </Box>
    </Box>
  );

  // ==========================================
  // RENDER PRINCIPAL
  // ==========================================
  return (
    <div>
      <Container maxWidth={isMobile ? false : 'xl'} sx={{ px: isMobile ? 1 : 3 }}>
        <MenuInicial />

        {!isMobile && (
          <Box sx={{ my: 3 }}>
            <Typography variant="h4" gutterBottom>
              <Inventory2TwoTone sx={{ mr: 1, verticalAlign: 'middle' }} />
              Productos
            </Typography>
          </Box>
        )}

        {loading ? (
          <Cargando />
        ) : (
          isMobile ? <MobileView /> : <DesktopView />
        )}

        {/* Diálogo de imagen ampliada */}
        <Dialog 
          open={!!expandedImage} 
          onClose={handleCloseImageDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Imagen del Producto
            <IconButton onClick={handleCloseImageDialog}>
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <img
              src={expandedImage}
              alt="Imagen ampliada"
              style={{ width: '100%', height: 'auto' }}
            />
          </DialogContent>
        </Dialog>

        {/* Diálogo de confirmación de eliminación */}
        <Dialog 
          open={deleteDialogOpen} 
          onClose={() => handleDeleteConfirmation(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Confirmar Eliminación</DialogTitle>
          <DialogContent>
            <Typography>¿Estás seguro de que quieres eliminar este producto?</Typography>
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
              Esta acción no se puede deshacer.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button 
              onClick={() => handleDeleteConfirmation(false)} 
              variant="outlined"
              fullWidth
            >
              Cancelar
            </Button>
            <Button 
              onClick={() => handleDeleteConfirmation(true)} 
              variant="contained" 
              color="error"
              fullWidth
            >
              Eliminar
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </div>
  );
};

export default ProductList;
